"use client"

import { useState, useRef } from "react"
import { Clock, User, Gem, Edit2, Save, X, Upload, Award, Twitter, Briefcase, Star } from "lucide-react"
import axios from "axios"
import { useAuth } from "../../contexts/AuthContext"
import { toast } from "react-toastify"
import { motion } from "framer-motion"

export function ProfileSidebar({ user = null, isMobile = false, onClose = null }) {
  const { token, setUser } = useAuth()
  const displayName = user ? `${user.firstname} ${user.lastname}` : "Unknown"
  const role = user?.role || "user"
  const profileImage = user?.profile_image
    ? `https://backend.emerald.azadnoori.com${user.profile_image}?t=${Date.now()}`
    : "/placeholder.svg?height=96&width=96"
  const plan = user?.plan || (role === "Admin" ? "Admin Pro" : "Free")
  const socialLinks = user?.socialLinks || { twitter: "", linkedin: "" }

  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    firstname: user?.firstname || "",
    lastname: user?.lastname || "",
    email: user?.email || "",
    password: "",
    confirmPassword: "",
    photo: null,
    twitter: socialLinks.twitter || "",
    linkedin: socialLinks.linkedin || "",
  })
  const [photoPreview, setPhotoPreview] = useState(null)
  const [errors, setErrors] = useState({})
  const fileInputRef = useRef(null)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    if (errors[name]) {
      setErrors({ ...errors, [name]: null })
    }
  }

  const handlePhotoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (!file.type.startsWith("image/")) {
        setErrors({ ...errors, photo: "Please upload an image file" })
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, photo: "Image size must be less than 5MB" })
        return
      }
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview)
      }
      const previewUrl = URL.createObjectURL(file)
      setFormData({ ...formData, photo: file })
      setPhotoPreview(previewUrl)
      setErrors({ ...errors, photo: null })
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.firstname.trim()) newErrors.firstname = "First name is required"
    if (!formData.lastname.trim()) newErrors.lastname = "Last name is required"
    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid"
    }
    if (formData.password && formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters"
    }
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    try {
      const data = new FormData()
      data.append("firstname", formData.firstname)
      data.append("lastname", formData.lastname)
      data.append("email", formData.email)
      if (formData.password) {
        data.append("password", formData.password)
        data.append("confirmPassword", formData.confirmPassword)
      }
      if (formData.photo) {
        data.append("profileImage", formData.photo)
      }
      data.append(
        "socialLinks",
        JSON.stringify({
          twitter: formData.twitter,
          linkedin: formData.linkedin,
        }),
      )

      const response = await axios.patch("https://backend.emerald.azadnoori.com/api/v1/users/updateProfile", data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      })

      setUser(response.data.data.user)
      setIsEditing(false)
      setFormData({
        firstname: response.data.data.user.firstname,
        lastname: response.data.data.user.lastname,
        email: response.data.data.user.email,
        password: "",
        confirmPassword: "",
        photo: null,
        twitter: response.data.data.user.socialLinks?.twitter || "",
        linkedin: response.data.data.user.socialLinks?.linkedin || "",
      })
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview)
      }
      setPhotoPreview(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
      toast.success("Profile updated successfully!")
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to update profile."
      toast.error(errorMessage)
    }
  }

  const toggleEdit = () => {
    setIsEditing(!isEditing)
    setErrors({})
    setPhotoPreview(null)
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview)
    }
    setFormData({
      firstname: user?.firstname || "",
      lastname: user?.lastname || "",
      email: user?.email || "",
      password: "",
      confirmPassword: "",
      photo: null,
      twitter: socialLinks.twitter || "",
      linkedin: socialLinks.linkedin || "",
    })
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleUpgradePlan = () => {
    const message = `Hello, I'm ${displayName} from Dubai Panjshir EmeraldVault, currently on the ${plan} plan. I'm interested in upgrading my plan to access more features. Please provide details on available plans and pricing.`
    const encodedMessage = encodeURIComponent(message)
    const whatsappUrl = `https://wa.me/+93729599599?text=${encodedMessage}`
    try {
      window.open(whatsappUrl, "_blank")
    } catch (error) {
      toast.error("Failed to open WhatsApp. Please try again.")
    }
  }

  // Animation variants for framer-motion
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  }

  // Static achievements (no dependency on stats)
  const achievements = [
    { name: "First Login", icon: Award, earned: true },
    { name: "Profile Complete", icon: Star, earned: user?.email && user?.firstname && user?.lastname },
    { name: "Social Star", icon: Twitter, earned: !!socialLinks.twitter || !!socialLinks.linkedin },
  ]

  if (isMobile) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Profile</h2>
          {onClose && (
            <button onClick={onClose} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
              <X size={20} />
            </button>
          )}
        </div>

        {/* Profile content - same as desktop but optimized for mobile */}
        <div className="flex-1 overflow-y-auto">
          <motion.div variants={sectionVariants} className="flex flex-col items-center mb-8">
            {!isEditing ? (
              <>
                <div className="relative h-20 w-20 mb-4 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 p-1 transition-transform hover:scale-105">
                  <div className="h-full w-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                    <img
                      src={profileImage || "/placeholder.svg"}
                      alt={`${displayName}'s profile`}
                      className="h-full w-full object-cover"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        e.target.src = "/placeholder.svg?height=96&width=96"
                      }}
                    />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-800 transition-colors hover:text-blue-600">{displayName}</h3>
                <span
                  className={`mt-2 inline-block px-3 py-1 text-sm font-semibold rounded-full ${
                    role === "Admin" ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"
                  }`}
                >
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </span>
                <button
                  onClick={toggleEdit}
                  className="mt-4 flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <Edit2 size={16} className="mr-1" />
                  Edit Profile
                </button>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="w-full space-y-4">
                <div className="flex flex-col items-center">
                  <div className="relative h-24 w-24 mb-4 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 p-1">
                    <div className="h-full w-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                      {photoPreview ? (
                        <img
                          src={photoPreview || "/placeholder.svg"}
                          alt="Profile preview"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <img
                          src={profileImage || "/placeholder.svg"}
                          alt={`${displayName}'s profile`}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                  <label className="cursor-pointer bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-emerald-700 transition-colors">
                    <Upload size={18} />
                    Change Photo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                      ref={fileInputRef}
                    />
                  </label>
                  {errors.photo && <p className="mt-2 text-sm text-red-600">{errors.photo}</p>}
                </div>
                <div>
                  <input
                    type="text"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleInputChange}
                    className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.firstname ? "border-red-500" : ""
                    }`}
                    placeholder="First Name"
                  />
                  {errors.firstname && <p className="text-sm text-red-600">{errors.firstname}</p>}
                </div>
                <div>
                  <input
                    type="text"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleInputChange}
                    className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.lastname ? "border-red-500" : ""
                    }`}
                    placeholder="Last Name"
                  />
                  {errors.lastname && <p className="text-sm text-red-600">{errors.lastname}</p>}
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.email ? "border-red-500" : ""
                    }`}
                    placeholder="Email"
                  />
                  {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
                </div>
                <div>
                  <input
                    type="text"
                    name="twitter"
                    value={formData.twitter}
                    onChange={handleInputChange}
                    className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.twitter ? "border-red-500" : ""
                    }`}
                    placeholder="Twitter URL (optional)"
                  />
                  {errors.twitter && <p className="text-sm text-red-600">{errors.twitter}</p>}
                </div>
                <div>
                  <input
                    type="text"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleInputChange}
                    className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.linkedin ? "border-red-500" : ""
                    }`}
                    placeholder="LinkedIn URL (optional)"
                  />
                  {errors.linkedin && <p className="text-sm text-red-600">{errors.linkedin}</p>}
                </div>
                <div>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.password ? "border-red-500" : ""
                    }`}
                    placeholder="New Password (optional)"
                  />
                  {errors.password && <p className="text-sm text-red-600">{errors.password}</p>}
                </div>
                <div>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.confirmPassword ? "border-red-500" : ""
                    }`}
                    placeholder="Confirm Password"
                  />
                  {errors.confirmPassword && <p className="text-sm text-red-600">{errors.confirmPassword}</p>}
                </div>
                <div className="flex justify-between">
                  <motion.button
                    type="submit"
                    className="flex items-center bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Save size={16} className="mr-1" />
                    Save
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={toggleEdit}
                    className="flex items-center bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <X size={16} className="mr-1" />
                    Cancel
                  </motion.button>
                </div>
              </form>
            )}
          </motion.div>

          {/* Decorative Divider */}
          <motion.div variants={sectionVariants} className="relative my-6">
            <hr className="border-t border-gray-200" />
            <div className="absolute inset-0 flex justify-center">
              <Gem className="h-5 w-5 text-purple-500 bg-white px-1" />
            </div>
          </motion.div>

          {/* User Plan Section */}
          <motion.div variants={sectionVariants} className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <Star className="h-5 w-5 text-yellow-500 mr-2" />
              Membership Plan
            </h4>
            <div
              className={`p-4 rounded-lg shadow-md text-center ${
                plan === "Admin Pro"
                  ? "bg-gradient-to-r from-red-500 to-pink-500 text-white"
                  : plan === "Premium"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white"
                    : "bg-gray-100 text-gray-800"
              }`}
            >
              <p className="text-lg font-bold">{plan}</p>
              <p className="text-sm mt-1">
                {plan === "Admin Pro"
                  ? "Full access with admin privileges"
                  : plan === "Premium"
                    ? "Enhanced features and support"
                    : "Basic access to core features"}
              </p>
              {plan !== "Admin Pro" && (
                <motion.button
                  onClick={handleUpgradePlan}
                  className="mt-3 bg-white text-blue-600 px-4 py-1 rounded-lg font-medium hover:bg-blue-50 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Upgrade Plan
                </motion.button>
              )}
            </div>
          </motion.div>

          {/* Achievements Section */}
          <motion.div variants={sectionVariants} className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <Award className="h-5 w-5 text-yellow-500 mr-2" />
              Achievements
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  className={`p-2 rounded-lg text-center ${
                    achievement.earned ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-500"
                  }`}
                  whileHover={{ scale: 1.1 }}
                >
                  <achievement.icon className="h-6 w-6 mx-auto mb-1" />
                  <p className="text-xs font-medium">{achievement.name}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Social Links */}
          {(socialLinks.twitter || socialLinks.linkedin) && !isEditing && (
            <motion.div variants={sectionVariants} className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <User className="h-5 w-5 text-blue-500 mr-2" />
                Connect
              </h4>
              <div className="flex justify-center space-x-4">
                {socialLinks.twitter && (
                  <a
                    href={socialLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-blue-400 transition-colors"
                  >
                    <Twitter size={24} />
                  </a>
                )}
                {socialLinks.linkedin && (
                  <a
                    href={socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-blue-700 transition-colors"
                  >
                    <Briefcase size={24} />
                  </a>
                )}
              </div>
            </motion.div>
          )}

          {/* Info Section */}
          <motion.div variants={sectionVariants} className="space-y-4">
            <div className="flex items-center space-x-3 text-gray-600">
              <Clock className="h-5 w-5 text-green-500" />
              <p className="text-sm font-medium">Active Member</p>
            </div>
          </motion.div>

          {/* Call-to-Action Button */}
          <motion.div variants={sectionVariants} className="mt-8">
            <motion.button
              className="w-full py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Full Profile
            </motion.button>
          </motion.div>
        </div>
      </div>
    )
  }

  // Return the original desktop version
  return (
    <motion.div
      className="hidden lg:block w-80 border-l border-gray-200 bg-gradient-to-b from-white to-gray-50 p-6 shadow-lg rounded-l-xl"
      initial="hidden"
      animate="visible"
      variants={sectionVariants}
    >
      {/* Profile Section */}
      <motion.div variants={sectionVariants} className="flex flex-col items-center mb-8">
        {!isEditing ? (
          <>
            <div className="relative h-24 w-24 mb-4 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 p-1 transition-transform hover:scale-105">
              <div className="h-full w-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                <img
                  src={profileImage || "/placeholder.svg"}
                  alt={`${displayName}'s profile`}
                  className="h-full w-full object-cover"
                  crossOrigin="anonymous"
                  onError={(e) => {
                    e.target.src = "/placeholder.svg?height=96&width=96"
                  }}
                />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-800 transition-colors hover:text-blue-600">{displayName}</h3>
            <span
              className={`mt-2 inline-block px-3 py-1 text-sm font-semibold rounded-full ${
                role === "Admin" ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"
              }`}
            >
              {role.charAt(0).toUpperCase() + role.slice(1)}
            </span>
            <button
              onClick={toggleEdit}
              className="mt-4 flex items-center text-blue-600 hover:text-blue-800 transition-colors"
            >
              <Edit2 size={16} className="mr-1" />
              Edit Profile
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="w-full space-y-4">
            <div className="flex flex-col items-center">
              <div className="relative h-24 w-24 mb-4 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 p-1">
                <div className="h-full w-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                  {photoPreview ? (
                    <img
                      src={photoPreview || "/placeholder.svg"}
                      alt="Profile preview"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <img
                      src={profileImage || "/placeholder.svg"}
                      alt={`${displayName}'s profile`}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
              </div>
              <label className="cursor-pointer bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-emerald-700 transition-colors">
                <Upload size={18} />
                Change Photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                  ref={fileInputRef}
                />
              </label>
              {errors.photo && <p className="mt-2 text-sm text-red-600">{errors.photo}</p>}
            </div>
            <div>
              <input
                type="text"
                name="firstname"
                value={formData.firstname}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  errors.firstname ? "border-red-500" : ""
                }`}
                placeholder="First Name"
              />
              {errors.firstname && <p className="text-sm text-red-600">{errors.firstname}</p>}
            </div>
            <div>
              <input
                type="text"
                name="lastname"
                value={formData.lastname}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  errors.lastname ? "border-red-500" : ""
                }`}
                placeholder="Last Name"
              />
              {errors.lastname && <p className="text-sm text-red-600">{errors.lastname}</p>}
            </div>
            <div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? "border-red-500" : ""
                }`}
                placeholder="Email"
              />
              {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
            </div>
            <div>
              <input
                type="text"
                name="twitter"
                value={formData.twitter}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  errors.twitter ? "border-red-500" : ""
                }`}
                placeholder="Twitter URL (optional)"
              />
              {errors.twitter && <p className="text-sm text-red-600">{errors.twitter}</p>}
            </div>
            <div>
              <input
                type="text"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  errors.linkedin ? "border-red-500" : ""
                }`}
                placeholder="LinkedIn URL (optional)"
              />
              {errors.linkedin && <p className="text-sm text-red-600">{errors.linkedin}</p>}
            </div>
            <div>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  errors.password ? "border-red-500" : ""
                }`}
                placeholder="New Password (optional)"
              />
              {errors.password && <p className="text-sm text-red-600">{errors.password}</p>}
            </div>
            <div>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  errors.confirmPassword ? "border-red-500" : ""
                }`}
                placeholder="Confirm Password"
              />
              {errors.confirmPassword && <p className="text-sm text-red-600">{errors.confirmPassword}</p>}
            </div>
            <div className="flex justify-between">
              <motion.button
                type="submit"
                className="flex items-center bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Save size={16} className="mr-1" />
                Save
              </motion.button>
              <motion.button
                type="button"
                onClick={toggleEdit}
                className="flex items-center bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <X size={16} className="mr-1" />
                Cancel
              </motion.button>
            </div>
          </form>
        )}
      </motion.div>

      {/* Decorative Divider */}
      <motion.div variants={sectionVariants} className="relative my-6">
        <hr className="border-t border-gray-200" />
        <div className="absolute inset-0 flex justify-center">
          <Gem className="h-5 w-5 text-purple-500 bg-white px-1" />
        </div>
      </motion.div>

      {/* User Plan Section */}
      <motion.div variants={sectionVariants} className="mb-6">
        <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
          <Star className="h-5 w-5 text-yellow-500 mr-2" />
          Membership Plan
        </h4>
        <div
          className={`p-4 rounded-lg shadow-md text-center ${
            plan === "Admin Pro"
              ? "bg-gradient-to-r from-red-500 to-pink-500 text-white"
              : plan === "Premium"
                ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white"
                : "bg-gray-100 text-gray-800"
          }`}
        >
          <p className="text-lg font-bold">{plan}</p>
          <p className="text-sm mt-1">
            {plan === "Admin Pro"
              ? "Full access with admin privileges"
              : plan === "Premium"
                ? "Enhanced features and support"
                : "Basic access to core features"}
          </p>
          {plan !== "Admin Pro" && (
            <motion.button
              onClick={handleUpgradePlan}
              className="mt-3 bg-white text-blue-600 px-4 py-1 rounded-lg font-medium hover:bg-blue-50 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Upgrade Plan
            </motion.button>
          )}
        </div>
      </motion.div>

      {/* Achievements Section */}
      <motion.div variants={sectionVariants} className="mb-6">
        <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
          <Award className="h-5 w-5 text-yellow-500 mr-2" />
          Achievements
        </h4>
        <div className="grid grid-cols-3 gap-2">
          {achievements.map((achievement, index) => (
            <motion.div
              key={index}
              className={`p-2 rounded-lg text-center ${
                achievement.earned ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-500"
              }`}
              whileHover={{ scale: 1.1 }}
            >
              <achievement.icon className="h-6 w-6 mx-auto mb-1" />
              <p className="text-xs font-medium">{achievement.name}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Social Links */}
      {(socialLinks.twitter || socialLinks.linkedin) && !isEditing && (
        <motion.div variants={sectionVariants} className="mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
            <User className="h-5 w-5 text-blue-500 mr-2" />
            Connect
          </h4>
          <div className="flex justify-center space-x-4">
            {socialLinks.twitter && (
              <a
                href={socialLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-400 transition-colors"
              >
                <Twitter size={24} />
              </a>
            )}
            {socialLinks.linkedin && (
              <a
                href={socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-700 transition-colors"
              >
                <Briefcase size={24} />
              </a>
            )}
          </div>
        </motion.div>
      )}

      {/* Info Section */}
      <motion.div variants={sectionVariants} className="space-y-4">
        <div className="flex items-center space-x-3 text-gray-600">
          <Clock className="h-5 w-5 text-green-500" />
          <p className="text-sm font-medium">Active Member</p>
        </div>
      </motion.div>

      {/* Call-to-Action Button */}
      <motion.div variants={sectionVariants} className="mt-8">
        <motion.button
          className="w-full py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          View Full Profile
        </motion.button>
      </motion.div>
    </motion.div>
  )
}
